import { World } from './world.js';
import { Renderer } from './renderer.js';
import { Player } from './player.js';
import { supportsBrushTools, supportsDragPainting } from './brush.js';
import { DayNightClock } from './day-night.js';
import { drawDarknessStencil, drawLightMask, needsLighting } from './light-mask.js';
import { NightCompositor } from './night-compositor.js';
import {
  createWorldSave,
  removeNpcFromSaves,
  repairWorldSave,
  WorldStorage,
} from './world-storage.js';
import { NpcRoster, npcNameTaken } from './npc/roster.js';
import { isFootBlocked } from './movement.js';
import { repairAppearance, saveLastAppearance } from './character/appearance.js';
import { composeAppearance, sheetToCanvas } from './character/compose.js';
import { CharacterCustomizer } from './character/customizer.js';
import { Palette } from './ui/palette.js';
import { formatRepairMessage, GameMenu } from './ui/game-menu.js';
import { TimePanel } from './ui/time-panel.js';

const DISPLAY_TILE_SIZE = 48;
const AREA_PREVIEW_FILL = 'rgba(107, 200, 242, 0.22)';
const AREA_PREVIEW_STROKE = '#6bc8f2';

export function areaBounds(anchor, cursor) {
  return {
    left: Math.min(anchor.tx, cursor.tx),
    right: Math.max(anchor.tx, cursor.tx),
    top: Math.min(anchor.ty, cursor.ty),
    bottom: Math.max(anchor.ty, cursor.ty),
  };
}

function disableSmoothing(ctx) {
  if (ctx) ctx.imageSmoothingEnabled = false;
}

export class Game {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.world = null;
    this.renderer = null;
    this.manifest = null;
    this.tileCatalog = null;
    this.tileSize = DISPLAY_TILE_SIZE;
    this.selectedBrush = { type: 'tile', tileId: 1 };
    this.brushSize = 1;
    this.brushMode = 'paint';
    this.paintAnchorTile = null;
    this.lastPaintedTile = null;
    this.paintingPointerId = null;
    this.areaPreview = null;
    this.hoveredTile = null;
    this.origin = { x: 0, y: 0 };
    this.player = null;
    this.storage = new WorldStorage();
    this.currentSave = null;
    this.saveTimer = null;
    this.ambientSaveTimer = null;
    /** @type {Map<string, { updatedAt: string, notes: string | null }>} repair labels by save id. */
    this._repairNotesCache = new Map();
    this._menuSaves = null;
    this.creatureAtlas = null;
    this.creatureManifest = null;
    this.customizer = null;
    this.npcRoster = new NpcRoster();
    /** @type {Map<number, HTMLCanvasElement>} composed sheets, keyed by roster id. */
    this.npcSprites = new Map();
    this.defaultBrush = null;
    this.pendingMenu = false;
    this.pendingWorldName = '';
    this.gameMenuStatus = '';
    this.clock = new DayNightClock();
    this.compositor = null;
    this.sceneCanvas = null;
    this.sceneCtx = null;
    this.maskCanvas = null;
    this.maskCtx = null;
    // Only built if WebGL compositing is unavailable.
    this.stencilCanvas = null;
    this.stencilCtx = null;
    this._scrubbingTime = false;
    this.palette = null;
    this.gameMenu = null;
    this.timePanel = null;
    this.viewportWidth = 0;
    this.viewportHeight = 0;
    this.renderScale = 1;
    this._canvasRect = null;
    this._resizeRaf = 0;
    this._deviceOrigin = { x: 0, y: 0 };
  }

  async init() {
    this.canvas = document.getElementById('game');
    this.ctx = this.canvas.getContext('2d', { alpha: false });
    this.sceneCanvas = document.createElement('canvas');
    this.sceneCtx = this.sceneCanvas.getContext('2d', { alpha: false });
    this.maskCanvas = document.createElement('canvas');
    this.maskCtx = this.maskCanvas.getContext('2d', { alpha: true });
    this.compositor = new NightCompositor();
    const [manifestResponse, atlas, creatureManifestResponse, creatureAtlas] = await Promise.all([
      fetch('/generated/tile-atlas.json'),
      this._loadImage('/generated/tile-atlas.png'),
      fetch('/generated/creature-atlas.json'),
      this._loadImage('/generated/creature-atlas.png'),
    ]);
    if (!manifestResponse.ok) throw new Error('Could not load tile atlas manifest');
    if (!creatureManifestResponse.ok) throw new Error('Could not load creature atlas manifest');
    this.manifest = await manifestResponse.json();
    this.creatureManifest = await creatureManifestResponse.json();
    this.creatureAtlas = creatureAtlas;
    this.tileCatalog = new Map(this.manifest.tiles.map((tile) => [tile.id, tile]));
    this.renderer = new Renderer(this.sceneCtx, this.tileSize, this.manifest, atlas);
    this.renderer.tiles = this.tileCatalog;
    this.player = new Player(null);
    this._loadLatestWorld();
    this._setAppearance(this.player.appearance);
    const initialFoundation = this.manifest.tiles.find((tile) => tile.channel === 'foundation');
    this.defaultBrush = { type: 'tile', tileId: initialFoundation.id };
    this.selectedBrush = this.defaultBrush;

    this._resizeCanvas();
    window.addEventListener('resize', () => this._scheduleResize());
    window.addEventListener('scroll', () => { this._canvasRect = null; }, true);
    this.canvas.addEventListener('pointerdown', (event) => this._onPointerDown(event));
    this.canvas.addEventListener('pointermove', (event) => this._onPointerMove(event));
    this.canvas.addEventListener('pointerup', (event) => this._stopPainting(event, true));
    this.canvas.addEventListener('pointercancel', (event) => this._stopPainting(event, false));
    this.canvas.addEventListener('dragstart', (event) => event.preventDefault());
    window.addEventListener('keydown', (event) => this._onKey(event, true));
    window.addEventListener('keyup', (event) => this._onKey(event, false));
    window.addEventListener('blur', () => this.player.keys.clear());
    this.canvas.addEventListener('pointerleave', () => {
      this.hoveredTile = null;
      this._updateCursor();
    });
    this.palette = new Palette(this);
    this.palette.init();
    this.gameMenu = new GameMenu(this);
    this.gameMenu.init();
    this.timePanel = new TimePanel(this);
    this.timePanel.init();
    this._initCustomizer();
    window.addEventListener('beforeunload', () => this._saveNow());
    // beforeunload does not reliably fire on mobile; a hidden tab is the last
    // dependable moment to persist.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this._saveNow();
    });
    if (this.pendingMenu) {
      this.gameMenu.open();
      this.pendingMenu = false;
    }
  }

  _loadLatestWorld() {
    const saves = this.storage.list()
      .sort((a, b) => String(b?.updatedAt).localeCompare(String(a?.updatedAt)));
    for (const save of saves) {
      try {
        this._restoreSave(save);
        this._saveNow();
        return;
      } catch (error) {
        console.warn(`Saved world ${save?.name ?? save?.id ?? '(unknown)'} cannot be loaded.`, error);
      }
    }
    this.world = new World(this.tileCatalog);
    this.clock.reset();
    this.currentSave = null;
    this.pendingMenu = true;
  }

  _uiBlocking() {
    return Boolean(this.customizer?.open || this.gameMenu?.isOpen());
  }

  _initCustomizer() {
    this.customizer = new CharacterCustomizer({
      root: document.getElementById('character-customizer'),
      onConfirm: (appearance, mode, npc) => this._onAppearanceConfirm(appearance, mode, npc),
      onCancel: (mode) => {
        this.player.keys.clear();
        if (mode === 'new-world') this.pendingWorldName = '';
        if (mode === 'new-world' && !this.currentSave) this.gameMenu.open();
      },
      onDelete: (npcId) => this._deleteNpc(npcId),
      validateName: (name, mode, npcId) => (
        mode === 'npc' || mode === 'npc-edit'
          ? (npcNameTaken(this.npcRoster.list(), name, npcId) ? 'An NPC already has that name.' : '')
          : ''
      ),
    });
  }

  _setAppearance(appearance) {
    this.player.appearance = repairAppearance(appearance);
    this.player.image = sheetToCanvas(composeAppearance(this.player.appearance));
  }

  _onAppearanceConfirm(appearance, mode, npc) {
    // NPC modes must not clobber the player's remembered look.
    if (mode === 'npc' || mode === 'npc-edit') {
      this._saveNpc(appearance, mode, npc);
      return;
    }
    saveLastAppearance(appearance);
    if (mode === 'new-world') {
      this.world = new World(this.tileCatalog);
      this.player.x = 0.5;
      this.player.y = 0.5;
      this.player.keys.clear();
      this.player.appearance = appearance;
      this.clock.reset();
      this.currentSave = createWorldSave(
        this.world,
        this.player,
        new Date(),
        this.clock.serialize(),
        this.pendingWorldName,
      );
      this.pendingWorldName = '';
      this.gameMenu.clearNewWorldName();
      this.palette.refreshNpcs();
    }
    this._setAppearance(appearance);
    this._saveNow();
    this.gameMenu.refresh();
    this._updateCursor();
  }

  _restoreSave(save) {
    const repaired = repairWorldSave(this.tileCatalog, save);
    this.world = repaired.world;
    this.currentSave = repaired.save;
    this.player.x = save.player.x;
    this.player.y = save.player.y;
    this.player.keys.clear();
    this._setAppearance(repaired.save.player.appearance);
    this.clock.restore(repaired.save.dayNight);
    this._bindNpcDefinitions();
    if (repaired.removedTiles || repaired.removedCells || repaired.removedCreatures || repaired.removedNpcs) {
      this.storage.save(repaired.save);
    }
    return repaired;
  }

  _loadWorld(id) {
    const save = this.storage.get(id);
    if (!save || save.id === this.currentSave?.id) return;
    this._saveNow();
    try {
      const repaired = this._restoreSave(save);
      this.gameMenuStatus = formatRepairMessage(save.name, repaired);
      this.gameMenu.refresh(this.gameMenuStatus);
      this.gameMenu.close();
      this.palette.refreshNpcs();
      this._updateCursor();
    } catch (error) {
      console.warn('Could not load the selected world.', error);
      this.gameMenu.refresh();
    }
  }

  _scheduleSave() {
    if (this.saveTimer) return;
    this.saveTimer = setTimeout(() => this._saveNow(), 300);
  }

  /**
   * Ambient motion (the player strolling, animals grazing) trickles into the
   * save on a slow cadence. Serializing the whole world plus rewriting the
   * multi-world blob at the edit cadence would otherwise run several times a
   * second forever, because some creature is almost always walking.
   */
  _scheduleAmbientSave() {
    if (this.saveTimer || this.ambientSaveTimer) return;
    this.ambientSaveTimer = setTimeout(() => this._saveNow(), 10000);
  }

  _saveNow() {
    if (!this.currentSave || !this.world || !this.player) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = null;
    clearTimeout(this.ambientSaveTimer);
    this.ambientSaveTimer = null;
    this.currentSave = {
      ...this.currentSave,
      updatedAt: new Date().toISOString(),
      world: this.world.serialize(),
      player: {
        ...this.currentSave.player,
        x: Math.round(this.player.x * 100) / 100,
        y: Math.round(this.player.y * 100) / 100,
        appearance: repairAppearance(this.player.appearance),
      },
      dayNight: this.clock.serialize(),
    };
    try {
      this.storage.save(this.currentSave);
    } catch (error) {
      console.warn('Could not save world to local storage.', error);
      const status = this.gameMenu?.hudStatus ?? document.getElementById('world-status');
      if (status) status.textContent = 'Saving failed — browser storage may be full.';
    }
  }

  _loadImage(url) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error(`Could not load ${url}`));
      image.src = url;
    });
  }

  _npcSprite(npc) {
    let sprite = this.npcSprites.get(npc.id);
    if (!sprite) {
      sprite = sheetToCanvas(composeAppearance(npc.appearance));
      this.npcSprites.set(npc.id, sprite);
    }
    return sprite;
  }

  /** Point every placed NPC at its current roster definition. */
  _bindNpcDefinitions() {
    const byId = new Map(this.npcRoster.list().map((npc) => [npc.id, npc]));
    for (const placed of this.world.npcs) {
      const definition = byId.get(placed.npcId);
      placed.setDefinition(definition);
      if (definition) this._npcSprite(definition);
    }
  }

  _saveNpc(appearance, mode, draft) {
    const npc = mode === 'npc'
      ? this.npcRoster.add(appearance, draft.behavior, draft.wanderRadius)
      : this.npcRoster.update(draft.id, {
        appearance,
        behavior: draft.behavior,
        wanderRadius: draft.wanderRadius,
      });
    if (npc) {
      this.npcSprites.delete(npc.id);
      this._bindNpcDefinitions();
    }
    this.palette.refreshNpcs();
    this._saveNow();
    this._updateCursor();
  }

  _deleteNpc(npcId) {
    this.npcRoster.remove(npcId);
    this.npcSprites.delete(npcId);
    this.world.removeNpc(npcId);
    if (this.selectedBrush.type === 'npc' && this.selectedBrush.npcId === npcId) {
      this.selectedBrush = this.defaultBrush;
    }
    this.palette.refreshNpcs();
    this._saveNow();
    // Other worlds would otherwise report this NPC as incompatible on load.
    removeNpcFromSaves(this.storage, npcId);
    this._updateCursor();
  }

  _scheduleResize() {
    if (this._resizeRaf) return;
    this._resizeRaf = requestAnimationFrame(() => {
      this._resizeRaf = 0;
      this._resizeCanvas();
    });
  }

  _resizeCanvas() {
    const renderScale = Math.max(1, Math.ceil(window.devicePixelRatio || 1));
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.renderScale = renderScale;
    this.viewportWidth = width;
    this.viewportHeight = height;
    this._canvasRect = this.canvas.getBoundingClientRect();

    this.canvas.width = width * renderScale;
    this.canvas.height = height * renderScale;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.ctx.setTransform(renderScale, 0, 0, renderScale, 0, 0);
    disableSmoothing(this.ctx);

    const deviceWidth = width * renderScale;
    const deviceHeight = height * renderScale;
    for (const canvas of [this.sceneCanvas, this.maskCanvas]) {
      if (!canvas) continue;
      canvas.width = deviceWidth;
      canvas.height = deviceHeight;
    }
    disableSmoothing(this.sceneCtx);
    disableSmoothing(this.maskCtx);
    this.compositor?.resize(deviceWidth, deviceHeight);
    this.origin.x = Math.floor(width / 2 - this.tileSize / 2);
    this.origin.y = Math.floor(height / 2 - this.tileSize / 2);
  }

  _screenToTile(event) {
    const rect = this._canvasRect ?? (this._canvasRect = this.canvas.getBoundingClientRect());
    return {
      tx: Math.floor((event.clientX - rect.left - this.origin.x) / this.tileSize),
      ty: Math.floor((event.clientY - rect.top - this.origin.y) / this.tileSize),
    };
  }

  _onKey(event, pressed) {
    if (pressed && event.key === 'Escape' && this.areaPreview) {
      event.preventDefault();
      this._abortAreaPaint();
      return;
    }
    if (this._uiBlocking()) return;
    if (
      event.target instanceof HTMLInputElement
      || event.target instanceof HTMLTextAreaElement
      || event.target instanceof HTMLSelectElement
    ) return;
    if (pressed && event.code === 'KeyF' && !event.repeat && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      this.palette.toggle();
      return;
    }
    if (!['KeyW', 'KeyA', 'KeyS', 'KeyD'].includes(event.code)) return;
    event.preventDefault();
    this.player.setKey(event.code, pressed);
  }

  _onPointerDown(event) {
    if (this._uiBlocking()) return;
    if (event.button !== 0) return;
    event.preventDefault();
    const tile = this._screenToTile(event);
    this.hoveredTile = tile;
    this._updateCursor();
    if (this.brushMode === 'area' && this._supportsBrushTools()) {
      this.paintingPointerId = event.pointerId;
      this.canvas.setPointerCapture(event.pointerId);
      this.paintAnchorTile = tile;
      this.lastPaintedTile = tile;
      this.areaPreview = { anchor: tile, cursor: tile };
      return;
    }
    this._paintLine(tile, tile);
    if (!this._supportsDragPainting()) return;
    this.paintingPointerId = event.pointerId;
    this.canvas.setPointerCapture(event.pointerId);
    this.paintAnchorTile = tile;
    this.lastPaintedTile = tile;
  }

  _onPointerMove(event) {
    if (this._uiBlocking()) return;
    const tile = this._screenToTile(event);
    this.hoveredTile = tile;
    this._updateCursor();
    if (event.pointerId !== this.paintingPointerId || !(event.buttons & 1)) return;
    if (this.areaPreview) {
      this.areaPreview.cursor = tile;
      this.lastPaintedTile = tile;
      return;
    }
    this._paintLine(this.lastPaintedTile ?? tile, tile);
    this.lastPaintedTile = tile;
  }

  _stopPainting(event, commit) {
    if (event.pointerId !== this.paintingPointerId) return;
    if (commit && this.areaPreview) {
      this._paintArea(this.areaPreview.anchor, this.areaPreview.cursor);
    }
    this.paintingPointerId = null;
    this.paintAnchorTile = null;
    this.lastPaintedTile = null;
    this.areaPreview = null;
  }

  _abortAreaPaint() {
    if (this.paintingPointerId != null) {
      try { this.canvas.releasePointerCapture(this.paintingPointerId); } catch { /* already released */ }
    }
    this.paintingPointerId = null;
    this.paintAnchorTile = null;
    this.lastPaintedTile = null;
    this.areaPreview = null;
  }

  _applyBrush(x, y) {
    // The gate keeps unknown brush types from falling into the collision
    // branch below, and skips no-op paints so they do not schedule saves.
    if (!this._canApplyBrush(x, y)) return;
    if (this.selectedBrush.type === 'tile') {
      const tile = this.world.tileCatalog.get(this.selectedBrush.tileId);
      if (tile?.channel === 'roof') this.world.fillBuildingRoof(x, y, this.selectedBrush.tileId);
      else if (tile?.paletteSection === 'interiorWalls') this.world.fillRoomBackWall(x, y, this.selectedBrush.tileId);
      else this.world.placeTile(x, y, this.selectedBrush.tileId);
    }
    else if (this.selectedBrush.type === 'elevation') this.world.setElevation(x, y, this.selectedBrush.elevation);
    else if (this.selectedBrush.type === 'transition') this.world.setTransition(x, y, this.selectedBrush.kind);
    else if (this.selectedBrush.type === 'eraseTransition') this.world.clearTransitionAt(x, y);
    else if (this.selectedBrush.type === 'eraseChannel') {
      this.world.eraseChannelAt(x, y, this.selectedBrush.channel);
    }
    else if (this.selectedBrush.type === 'eraseTerrain') this.world.eraseTerrainAt(x, y);
    else if (this.selectedBrush.type === 'eraseObjects') this.world.eraseObjectsAt(x, y);
    else if (this.selectedBrush.type === 'creature') {
      this.world.placeCreature(this.selectedBrush.species, x + 0.5, y + 0.5);
    }
    else if (this.selectedBrush.type === 'npc') {
      const placed = this.world.placeNpc(this.selectedBrush.npcId, x + 0.5, y + 0.5);
      if (placed) {
        placed.setDefinition(this.npcRoster.get(this.selectedBrush.npcId));
        this.selectedBrush = this.defaultBrush;
        this.palette.refreshNpcs();
      }
    }
    else if (this.selectedBrush.type === 'eraseCreature') {
      if (this.world.eraseCreaturesAt(x, y)) this.palette.refreshNpcs();
    }
    else this.world.setCollisionOverride(x, y, this.selectedBrush.state);
    this._scheduleSave();
  }

  _supportsBrushTools() {
    return supportsBrushTools(this.selectedBrush, this.world.tileCatalog);
  }

  _supportsDragPainting() {
    return supportsDragPainting(this.selectedBrush, this.world.tileCatalog);
  }

  _applyBrushAt(x, y) {
    const size = this._supportsBrushTools() && this.brushMode === 'paint' ? this.brushSize : 1;
    const radius = Math.floor(size / 2);
    for (let offsetY = -radius; offsetY <= radius; offsetY += 1) {
      for (let offsetX = -radius; offsetX <= radius; offsetX += 1) {
        this._applyBrush(x + offsetX, y + offsetY);
      }
    }
  }

  _canApplyBrush(x, y) {
    const brush = this.selectedBrush;
    if (brush.type === 'tile') return this.world.canPlaceTile(x, y, brush.tileId);
    if (brush.type === 'transition') return this.world.canSetTransition(x, y, brush.kind);
    if (brush.type === 'eraseTransition') return Boolean(this.world.getTransition(x, y));
    if (brush.type === 'eraseChannel') return Boolean(this.world.getPlacement(x, y, brush.channel));
    if (brush.type === 'eraseTerrain') {
      return Boolean(this.world.getPlacement(x, y, 'outdoorFloor')
        || this.world.getPlacement(x, y, 'surface')
        || this.world.getPlacement(x, y, 'foundation'));
    }
    if (brush.type === 'eraseObjects') {
      return Boolean(this.world.getPlacement(x, y, 'onGround')
        || this.world.getPlacement(x, y, 'treeWood'));
    }
    if (brush.type === 'elevation') return this.world.canSetElevation(x, y, brush.elevation);
    if (brush.type === 'collision') return this.world.getCollisionOverride(x, y) !== brush.state;
    if (brush.type === 'creature') return this.world.canPlaceCreature(x + 0.5, y + 0.5);
    // Unlike animals, an NPC collides — one dropped inside a wall would be stuck.
    if (brush.type === 'npc') {
      return !this.world.isNpcPlaced(brush.npcId) && !isFootBlocked(this.world, x + 0.5, y + 0.5);
    }
    if (brush.type === 'eraseCreature') return this.world.hasCreatureAt(x, y);
    return false;
  }

  _updateCursor() {
    const isInvalid = this.hoveredTile
      && !this._canApplyBrush(this.hoveredTile.tx, this.hoveredTile.ty);
    this.canvas.classList.toggle('cursor-invalid', Boolean(isInvalid));
  }

  _paintLine(start, end) {
    let x = start.tx;
    let y = start.ty;
    const dx = Math.abs(end.tx - x);
    const dy = Math.abs(end.ty - y);
    const sx = x < end.tx ? 1 : -1;
    const sy = y < end.ty ? 1 : -1;
    let error = dx - dy;
    while (true) {
      this._applyBrushAt(x, y);
      if (x === end.tx && y === end.ty) break;
      const doubledError = error * 2;
      if (doubledError > -dy) { error -= dy; x += sx; }
      if (doubledError < dx) { error += dx; y += sy; }
    }
    this._updateCursor();
  }

  _paintArea(start, end) {
    const { left, right, top, bottom } = areaBounds(start, end);
    for (let y = top; y <= bottom; y += 1) {
      for (let x = left; x <= right; x += 1) this._applyBrush(x, y);
    }
    this._updateCursor();
  }

  _drawAreaPreview() {
    if (!this.areaPreview) return;
    const { left, right, top, bottom } = areaBounds(this.areaPreview.anchor, this.areaPreview.cursor);
    const x = this.origin.x + left * this.tileSize;
    const y = this.origin.y + top * this.tileSize;
    const width = (right - left + 1) * this.tileSize;
    const height = (bottom - top + 1) * this.tileSize;
    const ctx = this.ctx;
    ctx.save();
    ctx.fillStyle = AREA_PREVIEW_FILL;
    ctx.strokeStyle = AREA_PREVIEW_STROKE;
    ctx.lineWidth = 2;
    ctx.fillRect(x, y, width, height);
    ctx.strokeRect(x + 1, y + 1, width - 2, height - 2);
    ctx.restore();
  }

  advanceClock(elapsedSeconds) {
    if (!this.clock || this._scrubbingTime) return;
    this.clock.update(elapsedSeconds);
  }

  update(dt) {
    if (!this.player) return;
    if (this._uiBlocking()) {
      this.player.keys.clear();
      return;
    }
    const previousX = this.player.x;
    const previousY = this.player.y;
    this.player.update(dt, this.world);
    const touched = new Set([
      `${Math.floor(this.player.x - 0.2)},${Math.floor(this.player.y + 0.2)}`,
      `${Math.floor(this.player.x + 0.2)},${Math.floor(this.player.y + 0.2)}`,
    ]);
    for (const cell of touched) {
      const [x, y] = cell.split(',').map(Number);
      const tile = this.renderer.tiles.get(this.world.getLayer(x, y, 'onGround'));
      if (tile?.animation?.interactionFrameDurationMs) this.renderer.triggerDecoration(x, y);
    }
    let creatureMoved = false;
    for (const creature of this.world.creatures) {
      const x = creature.x;
      const y = creature.y;
      creature.update(dt, this.world);
      if (creature.x !== x || creature.y !== y) creatureMoved = true;
    }
    for (const npc of this.world.npcs) {
      const x = npc.x;
      const y = npc.y;
      npc.update(dt, this.world);
      if (npc.x !== x || npc.y !== y) creatureMoved = true;
    }
    if (this.player.x !== previousX || this.player.y !== previousY || creatureMoved) {
      this._scheduleAmbientSave();
    }
    this.origin.x = Math.floor(this.viewportWidth / 2 - this.player.x * this.tileSize);
    this.origin.y = Math.floor(this.viewportHeight / 2 - this.player.y * this.tileSize);
  }

  /**
   * The Canvas-2D fallback needs darkness baked into an alpha channel it can
   * use as a stencil; the WebGL path reads the mask's channels directly and
   * pays nothing for this.
   */
  _darknessStencil(width, height) {
    if (this.compositor?.gl) return null;
    if (!this.stencilCanvas) {
      this.stencilCanvas = document.createElement('canvas');
      this.stencilCtx = this.stencilCanvas.getContext('2d', { alpha: true });
    }
    if (this.stencilCanvas.width !== width) this.stencilCanvas.width = width;
    if (this.stencilCanvas.height !== height) this.stencilCanvas.height = height;
    disableSmoothing(this.stencilCtx);
    return this.stencilCanvas;
  }

  render() {
    if (!this.renderer) return;
    // light-mask.js resets ctx.setTransform, so scene/mask/stencil all draw in
    // device pixels with a scaled tileSize rather than a CSS-pixel transform.
    const scale = this.renderScale;
    const width = this.viewportWidth;
    const height = this.viewportHeight;
    const deviceWidth = width * scale;
    const deviceHeight = height * scale;
    const deviceTile = this.tileSize * scale;
    this._deviceOrigin.x = this.origin.x * scale;
    this._deviceOrigin.y = this.origin.y * scale;
    this.renderer.tileSize = deviceTile;
    this.renderer.render(
      this.world,
      this._deviceOrigin,
      deviceWidth,
      deviceHeight,
      () => this._drawActors(this._deviceOrigin, deviceTile),
      this.player,
    );
    const nightStrength = this.clock.nightStrength;
    disableSmoothing(this.ctx);
    // Interiors are dark at every hour, so daylight no longer skips the mask —
    // it is still skipped outdoors at noon, where it would be a no-op.
    if (needsLighting(this.renderer, this.world, this.player, nightStrength)) {
      const lighting = {
        renderer: this.renderer,
        world: this.world,
        origin: this._deviceOrigin,
        tileSize: deviceTile,
        width: deviceWidth,
        height: deviceHeight,
        actorPosition: this.player,
        nightStrength,
      };
      drawLightMask(this.maskCtx, lighting);
      const stencil = this._darknessStencil(deviceWidth, deviceHeight);
      if (stencil) drawDarknessStencil(this.stencilCtx, lighting);
      const composited = this.compositor.composite(
        this.sceneCanvas, this.maskCanvas, nightStrength, undefined, stencil,
      );
      this.ctx.drawImage(composited, 0, 0, width, height);
    } else {
      this.ctx.drawImage(this.sceneCanvas, 0, 0, width, height);
    }
    this._drawAreaPreview();
    this.timePanel?.sync();
  }

  _drawActors(origin, tileSize) {
    const ctx = this.sceneCtx;
    const actors = [
      {
        x: this.player.x,
        y: this.player.y,
        draw: () => this.player.draw(ctx, origin, tileSize),
      },
      ...this.world.creatures.map((creature) => ({
        x: creature.x,
        y: creature.y,
        draw: () => creature.draw(
          ctx, origin, tileSize, this.creatureAtlas, this.creatureManifest,
        ),
      })),
      ...this.world.npcs.map((npc) => ({
        x: npc.x,
        y: npc.y,
        draw: () => npc.draw(ctx, origin, tileSize, this.npcSprites.get(npc.npcId)),
      })),
    ].sort((a, b) => a.y - b.y || a.x - b.x);
    for (const actor of actors) actor.draw();
  }
}
